import { Button, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Container, DrawerType, AccountInfo, Dispute } from '../../types';
import AppModals from '../components/Modals/AppModals';
import BlockingDialog from '../components/Modals/BlockingDialog';
const ModalContext = React.createContext<{
  drawerOpen: DrawerType;
  setDrawerOpen: React.Dispatch<React.SetStateAction<DrawerType>>;
  container: Container | undefined;
  setContainer: React.Dispatch<React.SetStateAction<Container | undefined>>;
  accountInfo: AccountInfo | undefined;
  setAccountInfo: React.Dispatch<React.SetStateAction<AccountInfo | undefined>>;
  dispute: Dispute | undefined;
  setDispute: React.Dispatch<React.SetStateAction<Dispute | undefined>>;
}>({
  drawerOpen: undefined,
  setDrawerOpen: () => { },
  container: undefined,
  setContainer: () => { },
  accountInfo: undefined,
  setAccountInfo: () => { },
  dispute: undefined,
  setDispute: () => { },
});
export default ModalContext;

export const ModalServiceProvider = ({ children }: { children: React.ReactNode }) => {
  const [drawerOpen, setDrawerOpen] = useState<DrawerType>(undefined);
  const [container, setContainer] = useState<Container | undefined>(undefined);
  const [accountInfo, setAccountInfo] = useState<AccountInfo | undefined>(undefined);
  const [dispute, setDispute] = useState<Dispute | undefined>(undefined);
  return (
    <>
      <ModalContext.Provider value={{ drawerOpen, setDrawerOpen, container, setContainer, accountInfo, setAccountInfo, dispute, setDispute }}>
        <ConfirmationServiceProvider>
          {children}
          <AppModals />
        </ConfirmationServiceProvider>
      </ModalContext.Provider>
    </>
  );
};

export const ConfirmationServiceContext = React.createContext<{
  showBockingModal: (id: string) => Promise<void>;
  showConfirmationModal: (
    confirmationOptions: ConfirmationOptions
  ) => Promise<{ confirmed: boolean; reason?: string } | void>;
  openId: string | undefined;
  setOpenId: (id: string | undefined) => void;
}>({
  showConfirmationModal: Promise.reject,
  showBockingModal: Promise.reject,
  openId: undefined,
  setOpenId: (id) => { },
});

type ConfirmationOptions = {
  title: string;
  contentText: string;
  // When set, a required free-text field is shown and its value comes back
  // as `reason` on confirm.
  reasonLabel?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  // Irreversible action: the confirm button renders in the error colour.
  destructive?: boolean;
  // Informational dialog: only the confirm button is shown.
  hideCancel?: boolean;
};
const ConfirmationServiceProvider = ({ children }: { children: React.ReactNode }) => {
  const [openId, setOpenId] = useState<string | undefined>(undefined);
  const [confirmationOptions, setConfirmationOptions] = useState<ConfirmationOptions>({
    title: '',
    contentText: '',
  });
  const awaitingPromiseRef = React.useRef<{
    resolve: (payload?: any) => void;
    reject: () => void;
  }>();

  useEffect(() => {
    if (openId === undefined && awaitingPromiseRef.current) {
      awaitingPromiseRef.current.resolve();
    }
  }, [openId]);

  const showBockingModal = (id: string) => {
    setOpenId(id);
    return new Promise<void>((resolve, reject) => {
      awaitingPromiseRef.current = { resolve, reject };
    });
  };

  const showConfirmationModal = (confirmationOptions: ConfirmationOptions) => {
    setConfirmationOptions(confirmationOptions);
    setOpenId('confirmation-modal');
    return new Promise<{ confirmed: boolean; reason?: string } | void>((resolve, reject) => {
      awaitingPromiseRef.current = { resolve, reject };
    });
  };

  const handleConfirm = (reason?: string) => {
    if (awaitingPromiseRef.current) {
      awaitingPromiseRef.current.resolve({ confirmed: true, reason });
    }
  };

  return (
    <ConfirmationServiceContext.Provider value={{ showBockingModal, openId, setOpenId, showConfirmationModal }}>
      {children}
      <ConfirmationModal
        confirmationOptions={confirmationOptions}
        onConfirm={handleConfirm}
        onCancel={() => setOpenId(undefined)}
      />
    </ConfirmationServiceContext.Provider>
  );
};

const ConfirmationModal = ({
  confirmationOptions,
  onConfirm,
  onCancel,
}: {
  confirmationOptions: ConfirmationOptions;
  onConfirm: (reason?: string) => void;
  onCancel: () => void;
}) => {
  const [reason, setReason] = useState('');
  // Fresh field each time the dialog is shown for a new question.
  useEffect(() => setReason(''), [confirmationOptions]);
  const needsReason = Boolean(confirmationOptions.reasonLabel);
  const { destructive, hideCancel } = confirmationOptions;
  return (
    <BlockingDialog
      id="confirmation-modal"
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 0.5 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>{confirmationOptions.title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
          {confirmationOptions.contentText}
        </Typography>
        {needsReason ? (
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            size="small"
            label={confirmationOptions.reasonLabel}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{ mt: 2.5 }}
          />
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        {hideCancel ? null : (
          <Button variant="text" color="inherit" onClick={onCancel} sx={{ textTransform: 'none', color: 'text.secondary' }}>
            {confirmationOptions.cancelLabel || 'No, go back'}
          </Button>
        )}
        <Button
          variant="contained"
          disableElevation
          color={destructive ? 'error' : 'primary'}
          autoFocus={!needsReason}
          disabled={needsReason && !reason.trim()}
          onClick={() => onConfirm(needsReason ? reason.trim() : undefined)}
          sx={{ textTransform: 'none', borderRadius: 2, px: 2.5 }}
        >
          {confirmationOptions.confirmLabel || 'Yes'}
        </Button>
      </DialogActions>
    </BlockingDialog>
  );
};
